'use client';

import { useEffect, useState } from 'react';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import {
  type MyChannelBookmark,
  subscribeToMyChannelBookmarks,
} from '../functions/bookmarks/subscribeToMyChannelBookmarks';

interface State {
  bookmarks: MyChannelBookmark[];
  isLoading: boolean;
  error: Error | undefined;
}

const kInitial: State = {
  bookmarks: [],
  isLoading: true,
  error: undefined,
};

/**
 * Live view of the current user's bookmarked messages in a single
 * channel — drives the Bookmarks panel. Newest-first ordering
 * mirrors the user's mental "what did I save recently" model.
 */
export function useMyChannelBookmarks(
  channelId: string | null | undefined,
): State {
  const userId = useCurrentUserId();
  const [state, setState] = useState<State>(kInitial);

  useEffect(() => {
    if (!userId || !channelId) {
      setState(kInitial);
      return;
    }
    setState(prev => ({ ...prev, isLoading: true, error: undefined }));
    return subscribeToMyChannelBookmarks(
      userId,
      channelId,
      bookmarks => {
        setState({ bookmarks, isLoading: false, error: undefined });
      },
      error => {
        setState({ bookmarks: [], isLoading: false, error });
      },
    );
  }, [userId, channelId]);

  return state;
}
